/**
 * NEXUMA — Sidebar Configuration
 *
 * Config-driven sidebar definition. Each item declares:
 * - requiresAny: user must have at least one of these permissions to see it
 * - requiresAll: user must have ALL of these permissions to see it
 * - scopes: (optional) contextual scopes that further filter visibility
 *
 * The SidebarRenderer component reads this config and filters
 * items based on the current user's resolved permissions.
 */

import { PERMISSIONS as P } from './permissionsConfig';

// Icons are strings here — SidebarRenderer maps them to MUI icons
// to keep this config pure-data (no JSX / no React import needed).
export const SIDEBAR_CONFIG = [
  // ── Overview ──────────────────────────────────────────────────────────────
  {
    id:    'overview',
    label: 'Overview',
    icon:  'SpeedIcon',
    to:    '/',
    exact: true,
    requiresAny: [P.DASHBOARD_VIEW_OWN, P.DASHBOARD_VIEW_TEAM, P.DASHBOARD_VIEW_COMPANY, P.DASHBOARD_VIEW_ALL],
  },

  // ── My Profile ──────────────────────────────────────────────────────────────
  // Self-service: opens the full Employee Detail report, auto-scoped to the
  // logged-in worker (admins see it with the worker picker via Reports).
  {
    id:    'my-profile',
    label: 'Profili im',
    icon:  'BadgeIcon',
    to:    '/home/reports/employee-detail',
    // No permission gate — every logged-in user can view & edit their own profile.
  },

  // ── Staff ────────────────────────────────────────────────────────────────
  {
    id:          'staff',
    label:       'Stafi',
    icon:        'BadgeIcon',
    section:     true,
    requiresAny: [P.EMPLOYEES_VIEW_OWN, P.EMPLOYEES_VIEW_TEAM, P.EMPLOYEES_VIEW_DEPARTMENT, P.EMPLOYEES_VIEW_COMPANY, P.EMPLOYEES_VIEW_ALL],
    children: [
      {
        id:          'workers',
        label:       'Punëtorët',
        icon:        'GroupsIcon',
        to:          '/workerlist',
        requiresAny: [P.EMPLOYEES_VIEW_DEPARTMENT, P.EMPLOYEES_VIEW_COMPANY, P.EMPLOYEES_VIEW_ALL],
      },
      /* ── Onboarding-u dhe radha personale: JASHTË menyje për tani ───────
       *
       * Moduli funksionon i plotë — rrugët, API-ja, lejet dhe testet janë në
       * vend, dhe `/onboarding`, `/my-tasks`, `/my-onboarding` hapen me URL.
       * Zërat u hoqën nga menyja me kërkesë: kjo pjesë do të rishikohet më
       * vonë, dhe deri atëherë nuk duhet t'u dalë përdoruesve si e përfunduar.
       *
       * Për ta rikthyer: hiq komentin këtu dhe te `SIDEBAR_EMPLOYEE_CONFIG`
       * më poshtë. Asgjë tjetër nuk duhet prekur.
       *
       * {
       *   id: 'my-tasks', label: 'Detyrat e mia',
       *   icon: 'AssignmentTurnedInIcon', to: '/my-tasks',
       * },
       * {
       *   id: 'onboarding', label: 'Onboarding', icon: 'HowToRegIcon',
       *   to: '/onboarding',
       *   requiresAny: [P.ONBOARDING_VIEW, P.ONBOARDING_MANAGE],
       * },
       */
      {
        id:          'org-chart',
        label:       'Organigrami',
        icon:        'AccountTreeIcon',
        to:          '/org-chart',
        requiresAny: [P.EMPLOYEES_VIEW_DEPARTMENT, P.EMPLOYEES_VIEW_COMPANY, P.EMPLOYEES_VIEW_ALL],
      },
      {
        id:          'team-management',
        label:       'Menaxhimi',
        icon:        'PeopleAltIcon',
        to:          '/team-management',
        requiresAny: [P.EMPLOYEES_VIEW_DEPARTMENT, P.EMPLOYEES_VIEW_COMPANY, P.EMPLOYEES_VIEW_ALL],
      },
      {
        id:          'worker-files',
        label:       'Dosjet',
        icon:        'FolderOpenIcon',
        to:          '/worker-files',
        requiresAny: [P.DOCUMENTS_VIEW_DEPARTMENT, P.DOCUMENTS_VIEW_COMPANY, P.EMPLOYEES_MANAGE],
      },
      {
        id:          'contracts',
        label:       'Kontratat',
        icon:        'ArticleIcon',
        to:          '/contractlist',
        requiresAny: [P.CONTRACTS_VIEW_OWN, P.CONTRACTS_VIEW_DEPARTMENT, P.CONTRACTS_VIEW_COMPANY],
      },
      {
        id:          'advancements',
        label:       'Karriera & Kompensimi',
        icon:        'TrendingUpIcon',
        to:          '/advancements',
        requiresAny: [P.EMPLOYEES_VIEW_DEPARTMENT, P.EMPLOYEES_VIEW_COMPANY],
      },
    ],
  },

  // ── Staff Performance ───────────────────────────────────────────────────────
  // Top-level section (the sidebar only renders section → leaf, so this cannot
  // be nested under Staff). Mirrors the worker-facing reference layout.
  {
    id:          'performance',
    label:       'Performanca e stafit',
    icon:        'EmojiEventsIcon',
    section:     true,
    // Edhe të drejtat e Trajnimeve: pa to, kush sheh vetëm trajnimet do ta
    // humbiste seksionin krejt pasi u zhvendos këtu nga Rekrutimi.
    requiresAny: [P.PERFORMANCE_VIEW_OWN, P.PERFORMANCE_VIEW_TEAM, P.PERFORMANCE_VIEW_COMPANY, P.RECRUITMENT_VIEW, P.EMPLOYEES_VIEW_COMPANY],
    children: [
      {
        id:          'my-evaluation',
        label:       'Vlerësimi im',
        icon:        'EmojiEventsIcon',
        to:          '/my-evaluation',
        requiresAny: [P.PERFORMANCE_VIEW_OWN, P.PERFORMANCE_VIEW_TEAM, P.PERFORMANCE_VIEW_COMPANY],
      },
      {
        // Ku eprori shkruan vlerësimin që punonjësi e lexon te "Vlerësimi im".
        // Kërkon të drejtën e shkrimit, jo të leximit: kush vetëm sheh
        // performancën nuk ka pse ta hapë formularin.
        id:          'team-evaluations',
        label:       'Vlerësimet e ekipit',
        icon:        'HowToRegIcon',
        to:          '/team-evaluations',
        requiresAny: [P.PERFORMANCE_CREATE, P.PERFORMANCE_EDIT, P.PERFORMANCE_MANAGE],
      },
      {
        id:          'performance-leaderboard',
        label:       'Tabela e performancës',
        icon:        'BarChartIcon',
        to:          '/performance',
        // Company/team leaderboard — kept out of the plain-employee scope.
        requiresAny: [P.PERFORMANCE_VIEW_TEAM, P.PERFORMANCE_VIEW_COMPANY],
      },
      {
        // Ciklet dhe shabllonet e vlerësimit. Një zë i vetëm, jo dy: kush
        // konfiguron ciklin konfiguron edhe kriteret e tij, dhe dy zëra do ta
        // ndanin të njëjtën punë në dy vende.
        id:          'performance-setup',
        label:       'Konfigurimi i vlerësimit',
        icon:        'TuneIcon',
        to:          '/performance-setup',
        requiresAny: [P.PERFORMANCE_MANAGE, P.PERFORMANCE_VIEW_COMPANY],
      },
      {
        // Trajnimet janë zhvillim i stafit ekzistues, jo pjesë e rekrutimit —
        // rrinë pranë vlerësimeve. Kush i shihte më parë i sheh edhe tani.
        id:          'trainings',
        label:       'Trajnimet',
        icon:        'SchoolIcon',
        to:          '/trainings',
        requiresAny: [P.RECRUITMENT_VIEW, P.EMPLOYEES_VIEW_COMPANY],
      },
    ],
  },

  // ── Attendance ────────────────────────────────────────────────────────────
  {
    id:          'attendance',
    label:       'Monitoring',
    icon:        'ScheduleIcon',
    section:     true,
    requiresAny: [P.ATTENDANCE_VIEW_OWN, P.ATTENDANCE_VIEW_TEAM, P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY],
    children: [
      // Attendance Panel hidden — module removed from UI (route /attendance kept intact)
      // {
      //   id:          'attendance-dashboard',
      //   label:       'Attendance Panel',
      //   icon:        'DashboardCustomizeIcon',
      //   to:          '/attendance',
      //   requiresAny: [P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY, P.ATTENDANCE_MANAGE],
      // },
      {
        id:          'presence-live',
        label:       'Hyrje / Dalje',
        icon:        'PendingActionsIcon',
        to:          '/presence-live',
        requiresAny: [P.ATTENDANCE_VIEW_TEAM, P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY],
      },
      {
        id:          'my-attendance',
        label:       'Prezenca ime',
        icon:        'FactCheckIcon',
        to:          '/my-attendance',
        requiresAny: [P.ATTENDANCE_VIEW_OWN, P.ATTENDANCE_VIEW_TEAM, P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY],
      },
      {
        id:          'work-schedules',
        label:       'Orari i Punës',
        icon:        'ScheduleIcon',
        to:          '/work-schedules',
        requiresAny: [P.ATTENDANCE_MANAGE, P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY],
      },
      {
        id:          'remote-work',
        label:       'Puna Hibride',
        icon:        'HomeWorkIcon',
        to:          '/remote-work',
        requiresAny: [P.ATTENDANCE_VIEW_OWN, P.ATTENDANCE_VIEW_TEAM, P.ATTENDANCE_VIEW_COMPANY],
      },
      {
        id:          'report-schedule-matrix',
        label:       'Orari Mujor Individual',
        icon:        'DateRangeIcon',
        to:          '/home/reports/work-schedule-matrix',
        // Employees may view their OWN monthly schedule (the report auto-scopes
        // non-admins to their own worker); admins/managers see everyone.
        requiresAny: [P.ATTENDANCE_VIEW_OWN, P.REPORTS_VIEW_OWN, P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.ATTENDANCE_VIEW_COMPANY],
      },
      {
        id:          'fte-oret-hub',
        label:       'Plani i Punës',
        icon:        'FolderOpenIcon',
        // Hapet te lista e projekteve; klikimi mbi projekt hap «Orët» e tij
        // (kërkesë e klientit) — jo më rrjetën mujore me «A».
        to:          '/fte-plan',
        extraPaths:  ['/oret'],
        requiresAny: [P.ATTENDANCE_MANAGE, P.ATTENDANCE_VIEW_COMPANY, P.EMPLOYEES_VIEW_COMPANY],
      },
    ],
  },

  // ── Approval Center ─────────────────────────────────────────────────────────
  // Standalone top-level module (promoted out of the Attendance section). Same
  // /inbox route; legacy /attendance-corrections still resolves and deep-links.
  {
    id:          'approval-center',
    label:       'Kërkesat dhe Aprovimet',
    icon:        'InboxIcon',
    to:          '/inbox',
    extraPaths:  ['/attendance-corrections'],
    // Leave permissions are listed here because the Approval Center absorbed
    // the retired "Miratimi i pushimeve" module. Without them a user who only
    // holds leave.approve/leave.reject (e.g. a team lead with no attendance
    // rights) would lose every route that can approve a leave request.
    requiresAny: [
      P.ATTENDANCE_MANAGE, P.ATTENDANCE_APPROVE, P.ATTENDANCE_VIEW_COMPANY,
      P.LEAVE_APPROVE, P.LEAVE_REJECT, P.LEAVE_MANAGE,
      P.LEAVE_VIEW_OWN, P.LEAVE_VIEW_TEAM, P.LEAVE_VIEW_DEPARTMENT, P.LEAVE_VIEW_COMPANY,
    ],
  },

  // ── Communication ─────────────────────────────────────────────────────────
  // No sidebar entry: chat lives in the floating panel bottom-right, which is
  // where it is actually used from. The full page (/communication) still exists
  // and is reached from that panel's ⤢ button — the route and its permissions
  // (communication.view.own / .view.company / .manage) are unchanged.

  // ── Leave ─────────────────────────────────────────────────────────────────
  {
    id:          'leave',
    label:       'Pushimet',
    icon:        'BeachAccessIcon',
    section:     true,
    requiresAny: [P.LEAVE_VIEW_OWN, P.LEAVE_VIEW_TEAM, P.LEAVE_VIEW_DEPARTMENT, P.LEAVE_VIEW_COMPANY],
    children: [
      // 'leave-requests' (/holidays) and 'leave-approvals' (/leave-approvals)
      // were retired — both duplicated the Approval Center at /inbox, which
      // reads the same GET /api/holidays feed and approves through the same
      // two-stage /api/holidays/:id/{supervisor,hr}-decision endpoints.
      // The submission form (/holidays/addholiday, "Kërko Leje" below) stays.
      {
        id:          'my-leaves',
        label:       'Pushimet e mia',
        icon:        'FactCheckIcon',
        to:          '/my-leaves',
        requiresAny: [P.LEAVE_VIEW_OWN],
      },
      {
        id:          'leave-balance',
        label:       'Balanca e pushimeve',
        icon:        'AssessmentIcon',
        to:          '/home/reports/leave-balance',
        requiresAny: [P.LEAVE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY],
      },
      {
        id:          'leave-adjustments',
        label:       'Rregullimet e pushimeve',
        icon:        'TuneIcon',
        to:          '/leave-adjustments',
        requiresAny: [P.LEAVE_ADJUSTMENTS_VIEW, P.LEAVE_ADJUSTMENTS_MANAGE],
      },
      {
        id:          'leave-policies',
        label:       'Politikat e pushimeve',
        icon:        'GavelIcon',
        to:          '/leave-policies',
        requiresAny: [P.LEAVE_MANAGE, P.SETTINGS_VIEW],
      },
      {
        id:          'leave-calendar',
        label:       'Kalendari i pushimeve',
        icon:        'CalendarMonthIcon',
        to:          '/leave-calendar',
        requiresAny: [P.LEAVE_VIEW_OWN, P.LEAVE_VIEW_TEAM, P.LEAVE_VIEW_DEPARTMENT, P.LEAVE_VIEW_COMPANY],
      },
    ],
  },

  // ── Projects ──────────────────────────────────────────────────────────────
  {
    id:          'projects',
    label:       'Projektet',
    icon:        'AccountTreeIcon',
    section:     true,
    requiresAny: [P.PROJECTS_VIEW_OWN, P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_COMPANY],
    children: [
      {
        id:          'projects-list',
        label:       'Lista e projekteve',
        icon:        'AccountTreeIcon',
        to:          '/projects',
        requiresAny: [P.PROJECTS_VIEW_OWN, P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_COMPANY],
      },
      // ── Pjesët e tjera janë komentuar përkohësisht — mbahet vetëm lista + shtimi i projekteve ──
      // {
      //   id:          'tasks-dashboard',
      //   label:       'Paneli',
      //   icon:        'HomeIcon',
      //   to:          '/tasks/dashboard',
      //   requiresAny: [P.PROJECTS_VIEW_OWN, P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_COMPANY],
      // },
      // {
      //   id:          'tasks-board',
      //   label:       'Bordi',
      //   icon:        'KanbanIcon',
      //   to:          '/tasks/board',
      //   requiresAny: [P.PROJECTS_VIEW_OWN, P.PROJECTS_VIEW_TEAM],
      // },
      // {
      //   id:          'tasks',
      //   label:       'Detyrat',
      //   icon:        'AssignmentIcon',
      //   to:          '/tasks',
      //   requiresAny: [P.PROJECTS_VIEW_OWN, P.PROJECTS_VIEW_TEAM],
      // },
      // {
      //   id:          'tickets',
      //   label:       'Bileta WFM',
      //   icon:        'InboxIcon',
      //   to:          '/tickets',
      //   requiresAny: [P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_COMPANY],
      // },
      {
        // Klientët janë referencë e projekteve: fusha «Klienti / Kompania» te
        // formulari lexon pikërisht këtë listë, dhe pa një vend ku shtohen ajo
        // do të mbetej përgjithmonë e zbrazët. Ekrani ekzistonte i plotë; i
        // mungonte vetëm hyrja.
        id:          'clients',
        label:       'Klientët',
        icon:        'BusinessIcon',
        to:          '/clients',
        requiresAny: [P.PROJECTS_VIEW_COMPANY, P.PROJECTS_MANAGE],
      },
      // {
      //   id:          'activities',
      //   label:       'Aktivitetet',
      //   icon:        'TimerIcon',
      //   to:          '/activities',
      //   requiresAny: [P.PROJECTS_VIEW_OWN, P.PROJECTS_VIEW_TEAM],
      // },
      // {
      //   id:          'time-tracking',
      //   label:       'Koha e punës',
      //   icon:        'TimerIcon',
      //   to:          '/time-tracking',
      //   requiresAny: [P.PROJECTS_VIEW_OWN, P.ATTENDANCE_VIEW_OWN],
      // },
    ],
  },

  // ── Recruitment ────────────────────────────────────────────────────────────
  {
    id:          'recruitment',
    label:       'Rekrutimi',
    icon:        'PeopleAltIcon',
    section:     true,
    requiresAny: [P.RECRUITMENT_VIEW, P.RECRUITMENT_MANAGE],
    children: [
      {
        // Hyrja e modulit: çka kërkon vëmendje sot, para listave të gjata.
        id:          'recruitment-overview',
        label:       'Përmbledhje',
        icon:        'QueryStatsIcon',
        to:          '/recruitment',
        requiresAny: [P.RECRUITMENT_VIEW, P.RECRUITMENT_MANAGE],
      },
      {
        id:          'job-postings',
        label:       'Pozitat',
        icon:        'WorkOutlineIcon',
        to:          '/job-postings',
        requiresAny: [P.RECRUITMENT_VIEW],
      },
      {
        id:          'applications',
        label:       'Kandidatët',
        icon:        'PersonSearchIcon',
        to:          '/applications',
        requiresAny: [P.RECRUITMENT_VIEW],
      },
      {
        id:          'interviews',
        label:       'Intervistat',
        icon:        'RecordVoiceOverIcon',
        to:          '/interviews',
        requiresAny: [P.RECRUITMENT_VIEW],
      },
      {
        // Ofertat kanë cikël të vetin — draft, miratim, dërgim, përgjigje — dhe
        // një listë e tyre është pyetja e përditshme e HR-së: cilat presin.
        id:          'offers',
        label:       'Ofertat',
        icon:        'WorkOutlineIcon',
        to:          '/offers',
        requiresAny: [P.RECRUITMENT_VIEW],
      },
      {
        // Përfundimet e suksesshme — jo kopje e Stafit. Aty ku Stafi thotë kush
        // punon sot, kjo thotë nga cila pozitë, cili aplikim dhe cila ofertë erdhi.
        id:          'hires',
        label:       'Punësimet',
        icon:        'HowToRegIcon',
        to:          '/hires',
        requiresAny: [P.RECRUITMENT_VIEW],
      },
      {
        // Kërkimi i njerëzve që kanë aplikuar më parë — përfshirë pozitat e
        // mbyllura. Mbyllja e një pozite është gjendje biznesi, jo fshirje.
        id:          'recruitment-history',
        label:       'Historiku i rekrutimit',
        icon:        'HistoryIcon',
        to:          '/recruitment-history',
        requiresAny: [P.RECRUITMENT_VIEW],
      },
    ],
  },

  // ── Finance ────────────────────────────────────────────────────────────────
  {
    id:          'finance',
    label:       'Financa',
    icon:        'AccountBalanceIcon',
    section:     true,
    requiresAny: [P.PAYROLL_VIEW_OWN, P.PAYROLL_VIEW_DEPARTMENT, P.PAYROLL_VIEW_COMPANY],
    children: [
      {
        id:          'salaries',
        label:       'Pagat',
        icon:        'PaymentsIcon',
        to:          '/salarieslist',
        requiresAny: [P.PAYROLL_VIEW_DEPARTMENT, P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
      },
      // Bonuset, Avancet dhe Rimbursimet ishin tri hyrje të veçanta. Tani janë
      // tabs brenda kësaj faqeje — menyja mban dy zëra, jo katër.
      {
        id:          'payroll-adjustments',
        label:       'Rregullimet e pagës',
        icon:        'TuneIcon',
        to:          '/payroll-adjustments',
        requiresAny: [P.PAYROLL_VIEW_DEPARTMENT, P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
      },
    ],
  },

  // ── Raportet ──────────────────────────────────────────────────────────────
  // Një modul i vetëm "Raportet", i ndarë në grupe si hub-i /home/reports
  // (Analitika · Koha · Paga · Punëtorët · ATK). Grupet janë nën-nivel
  // i palosshëm: SidebarRenderer i heq vetvetiu ata që mbeten bosh pas filtrit
  // të privilegjeve.
  {
    id:          'reports',
    label:       'Raportet',
    icon:        'AssessmentIcon',
    section:     true,
    requiresAny: [P.REPORTS_VIEW_OWN, P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
    children: [
      // ── Analitika & Produktiviteti ────────────────────────────────────────
      {
        id:          'reports-analytics',
        label:       'Analitika & Produktiviteti',
        icon:        'BarChartIcon',
        section:     true,
        group:       true,
        requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
        children: [
          {
            id:          'rep-analytics',
            label:       'Analitika',
            icon:        'QueryStatsIcon',
            to:          '/home/reports/analytics',
            requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
          {
            id:          'rep-worker-evaluation',
            label:       'Vlerësimi i Punëtorit',
            icon:        'StarIcon',
            to:          '/home/reports/worker-evaluation',
            requiresAny: [P.PERFORMANCE_VIEW_TEAM, P.PERFORMANCE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
          // Hequr përkohësisht: raporti lexon tabelën `tasks`, por moduli që e
          // mbush (Projektet → Detyrat / Bordi) është vetë i komentuar më lart,
          // ndaj tregonte vetëm mbetje testimi. Kthehu bashkë me atë modul.
          // {
          //   id:          'rep-task-management',
          //   label:       'Raporti i detyrave',
          //   icon:        'AssignmentIcon',
          //   to:          '/home/reports/task-management',
          //   requiresAny: [P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          // },
        ],
      },

      // ── Koha & Oraret ─────────────────────────────────────────────────────
      {
        id:          'reports-time',
        label:       'Koha & Oraret',
        icon:        'ScheduleIcon',
        section:     true,
        group:       true,
        requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL, P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY],
        children: [
          {
            id:          'rep-work-hours',
            label:       'Orët e Punës',
            icon:        'AccessTimeIcon',
            to:          '/home/reports/work-hours',
            requiresAny: [P.REPORTS_VIEW_COMPANY, P.ATTENDANCE_VIEW_COMPANY],
          },
          {
            id:          'rep-attendance-schedule',
            label:       'Raporti Attendance',
            icon:        'FactCheckIcon',
            to:          '/home/reports/attendance-schedule',
            requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY],
          },
          {
            // Attendance Rate ditore/javore/mujore — shifrat vijnë nga serveri.
            id:          'rep-attendance-rate',
            label:       'Attendance Rate',
            icon:        'FactCheckIcon',
            to:          '/home/reports/attendance-rate',
            requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL, P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY, P.ATTENDANCE_VIEW_TEAM],
          },
          {
            id:          'rep-attendance',
            label:       'Raporti i Prezencës',
            icon:        'EventAvailableIcon',
            to:          '/home/reports/attendance',
            requiresAny: [P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY],
          },
          {
            id:          'rep-overtime',
            label:       'Orët Shtesë',
            icon:        'MoreTimeIcon',
            to:          '/home/reports/overtime',
            requiresAny: [P.ATTENDANCE_VIEW_DEPARTMENT, P.ATTENDANCE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY],
          },
          {
            id:          'rep-project-hours',
            label:       'Orët e Projektit',
            icon:        'FolderOpenIcon',
            to:          '/home/reports/project-hours',
            requiresAny: [P.PROJECTS_VIEW_TEAM, P.PROJECTS_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY],
          },
        ],
      },

      // ── Paga & Kostot ─────────────────────────────────────────────────────
      {
        id:          'reports-payroll',
        label:       'Paga & Kostot',
        icon:        'PaymentsIcon',
        section:     true,
        group:       true,
        requiresAny: [P.PAYROLL_VIEW_DEPARTMENT, P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
        children: [
          {
            id:          'rep-hr-spend',
            label:       'Kosto pune',
            icon:        'AccountBalanceWalletIcon',
            to:          '/home/reports/hr-spend',
            requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
          },
          {
            id:          'rep-payroll',
            label:       'Raporti i Pagave',
            icon:        'PaymentsIcon',
            to:          '/home/reports/payroll',
            requiresAny: [P.PAYROLL_VIEW_DEPARTMENT, P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
          },
          {
            id:          'rep-monthly-costs',
            label:       'Kosto mujore',
            icon:        'GroupsIcon',
            to:          '/home/reports/monthly-costs',
            requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
          },
          {
            id:          'rep-creditor-monthly',
            label:       'Urdhëresë pagese',
            icon:        'ReceiptLongIcon',
            to:          '/home/reports/creditor-monthly',
            requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
          },
        ],
      },

      // ── Punëtorët & Pushimet ──────────────────────────────────────────────
      {
        id:          'reports-employees',
        label:       'Punëtorët & Pushimet',
        icon:        'BadgeIcon',
        section:     true,
        group:       true,
        requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
        children: [
          {
            id:          'rep-employee-detail',
            label:       'Raport i punëtorit',
            icon:        'AssignmentIndIcon',
            to:          '/home/reports/employee-detail',
            requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
          {
            id:          'rep-employee-summary',
            label:       'Paga & Pushime',
            icon:        'SummarizeIcon',
            to:          '/home/reports/employee-summary',
            requiresAny: [P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
          {
            id:          'rep-employees-cards',
            label:       'Kartela e punëtorit',
            icon:        'BadgeIcon',
            to:          '/home/reports/employees-cards',
            requiresAny: [P.REPORTS_VIEW_DEPARTMENT, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
          {
            id:          'rep-leave-ks',
            label:       'Pushimet (KS)',
            icon:        'BeachAccessIcon',
            to:          '/home/reports/leave-ks',
            requiresAny: [P.LEAVE_VIEW_DEPARTMENT, P.LEAVE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY],
          },
          {
            id:          'rep-leave-balance',
            label:       'Balanca e pushimeve',
            icon:        'DateRangeIcon',
            to:          '/home/reports/leave-balance',
            requiresAny: [P.LEAVE_VIEW_DEPARTMENT, P.LEAVE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY],
          },
          {
            id:          'rep-applications',
            label:       'Raport aplikimesh',
            icon:        'WorkOutlineIcon',
            to:          '/applications/reports',
            requiresAny: [P.RECRUITMENT_VIEW, P.RECRUITMENT_MANAGE],
          },
        ],
      },

      // ── ATK & Institucionale ──────────────────────────────────────────────
      {
        id:          'reports-atk',
        label:       'ATK & Institucionale',
        icon:        'AccountBalanceIcon',
        section:     true,
        group:       true,
        requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
        children: [
          {
            id:          'rep-atk-registrations',
            label:       'Regjistrime ATK',
            icon:        'CheckCircleIcon',
            to:          '/home/reports/atk-registrations',
            requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE, P.REPORTS_VIEW_COMPANY],
          },
          {
            id:          'rep-local-payroll-ks',
            label:       'Lista e pagave',
            icon:        'EuroIcon',
            to:          '/home/reports/local-payroll-ks',
            requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
          },
          {
            id:          'rep-atk-tm',
            label:       'TM1 – TM4',
            icon:        'TableChartIcon',
            to:          '/home/reports/atk-tm',
            requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
          },
          {
            id:          'rep-presence',
            label:       'Prezencë (Inspektorat)',
            icon:        'TimerIcon',
            to:          '/home/reports/presence',
            requiresAny: [P.ATTENDANCE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
          {
            id:          'rep-presence-logs',
            label:       'Prezencë (Evente)',
            icon:        'HistoryIcon',
            to:          '/home/reports/presence-logs',
            requiresAny: [P.ATTENDANCE_VIEW_COMPANY, P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
          {
            id:          'rep-kbpk',
            label:       'KBPK — Fondi Pensional',
            icon:        'SavingsIcon',
            to:          '/home/reports/kbpk',
            requiresAny: [P.PAYROLL_VIEW_COMPANY, P.PAYROLL_MANAGE],
          },
          {
            id:          'rep-inspektorat',
            label:       'Lista Punëtorëve',
            icon:        'PeopleAltIcon',
            to:          '/home/reports/inspektorat',
            requiresAny: [P.REPORTS_VIEW_COMPANY, P.REPORTS_VIEW_ALL],
          },
        ],
      },
    ],
  },

  // ── Vërejtjet ─────────────────────────────────────────────────────────────
  // Modul i vetin, jashtë "Raportet": nuk është raport për t'u lexuar, është
  // regjistër disiplinor ku shtohen e njoftohen vërejtje.
  {
    id:          'warnings',
    label:       'Vërejtjet',
    icon:        'WarningAmberIcon',
    to:          '/warnings',
    requiresAny: [P.WARNINGS_VIEW_DEPARTMENT, P.WARNINGS_VIEW_COMPANY],
  },

  // ── Asetet ────────────────────────────────────────────────────────────────
  // Rri pikërisht pas Vërejtjeve dhe jashtë "Raporteve", për të njëjtën arsye
  // si ato: nuk është tabelë për t'u lexuar, është regjistër ku pajisjet
  // dorëzohen e kthehen. Dhe rri pranë tyre sepse të dyja mbyllen te i njëjti
  // vend — dëmtimi ose moskthimi kalon te Rregullimet e pagës si ndalesë.
  {
    id:          'assets',
    label:       'Asetet',
    icon:        'Inventory2Icon',
    to:          '/assets',
    requiresAny: [P.ASSETS_VIEW_DEPARTMENT, P.ASSETS_VIEW_COMPANY, P.ASSETS_MANAGE],
  },

  // ── Shabllonet ────────────────────────────────────────────────────────────
  // Moduli `/templates` (kontrata, vendime, vërejtje) NUK shfaqet më te menuja
  // anësore — përgatitja e teksteve është punë e rrallë konfigurimi, jo një zë
  // i përditshëm navigimi. Faqja mbetet e gjallë dhe e mbrojtur: privilegjet i
  // deklaron vetë rruga te Dashboard.js, sepse pa një zë sidebar-i
  // `guardedRoutes.js` s'ka nga ku t'i nxjerrë.

  // ── Knowledge Docs ────────────────────────────────────────────────────────
  {
    id:          'knowledge',
    label:       'Rregullore & Procedura',
    icon:        'FolderSpecialIcon',
    section:     true,
    requiresAny: [P.DOCUMENTS_VIEW_OWN, P.DOCUMENTS_VIEW_DEPARTMENT, P.DOCUMENTS_VIEW_COMPANY],
    children: [
      {
        id:          'regulations',
        label:       'Rregulloret',
        icon:        'GavelIcon',
        to:          '/regulations',
        requiresAny: [P.DOCUMENTS_VIEW_OWN, P.DOCUMENTS_VIEW_DEPARTMENT, P.DOCUMENTS_VIEW_COMPANY],
      },
      {
        id:          'policies',
        label:       'Politikat',
        icon:        'PolicyIcon',
        to:          '/policies',
        requiresAny: [P.DOCUMENTS_VIEW_OWN, P.DOCUMENTS_VIEW_DEPARTMENT, P.DOCUMENTS_VIEW_COMPANY],
      },
      {
        id:          'procedures',
        label:       'Procedurat',
        icon:        'MenuBookIcon',
        to:          '/procedures',
        requiresAny: [P.DOCUMENTS_VIEW_OWN, P.DOCUMENTS_VIEW_DEPARTMENT, P.DOCUMENTS_VIEW_COMPANY],
      },
      {
        id:          'knowledge-base',
        label:       'Qendra e Njohurive',
        icon:        'LibraryBooksIcon',
        to:          '/knowledge-base',
        requiresAny: [P.DOCUMENTS_VIEW_OWN, P.DOCUMENTS_VIEW_DEPARTMENT, P.DOCUMENTS_VIEW_COMPANY],
      },
    ],
  },

  // ── System ────────────────────────────────────────────────────────────────
  {
    id:          'system',
    label:       'Sistemi',
    icon:        'TuneIcon',
    section:     true,
    requiresAny: [P.SETTINGS_VIEW, P.AUDIT_LOGS_VIEW_COMPANY, P.AUDIT_LOGS_VIEW_ALL, P.ROLES_VIEW],
    children: [
      {
        id:          'notifications-center',
        label:       'Njoftimet',
        icon:        'NotificationsIcon',
        to:          '/notifications-center',
        requiresAny: [P.SETTINGS_VIEW, P.EMPLOYEES_MANAGE],
      },
      {
        id:          'audit-log',
        label:       'Audit Log',
        icon:        'ManageSearchIcon',
        to:          '/audit-log',
        requiresAny: [P.AUDIT_LOGS_VIEW_COMPANY, P.AUDIT_LOGS_VIEW_ALL],
      },
      {
        // Hub i vetëm i qasjeve: tabet Përdoruesit · Rolet & Privilegjet · Pozitat & Qasjet.
        // Rrugët e vjetra /roles-permissions dhe /positions ridrejtohen te tabet përkatëse.
        id:          'user-management',
        label:       'Menaxhimi i Përdoruesve',
        icon:        'PeopleAltIcon',
        to:          '/user-management',
        requiresAny: [P.ROLES_VIEW, P.ROLES_MANAGE, P.EMPLOYEES_MANAGE],
      },
      {
        id:          'company-settings',
        label:       'Konfigurimi',
        icon:        'SettingsIcon',
        to:          '/company?tab=profili',
        // Rruga e vjetër e llojeve të punës ridrejton këtu; pa këtë, zëri te
        // menyja nuk ndriçohej kur njeriu hapej te ai tab.
        extraPaths:  ['/company', '/work-types'],
        requiresAny: [P.SETTINGS_VIEW, P.SETTINGS_MANAGE],
      },
      {
        id:          'module-management',
        label:       'Modulet & Fushat',
        icon:        'ExtensionIcon',
        to:          '/module-management',
        requiresAny: [P.SETTINGS_MANAGE],
        badge:       'NEW',
      },
    ],
  },

  // ── Helper ────────────────────────────────────────────────────────────────
  // Last entry, and deliberately TOP-LEVEL rather than inside "Sistemi":
  // that section requires settings/audit/roles permissions, so nesting Helper
  // there would hide it from exactly the people most likely to need to report
  // a problem.
  //
  // `action` opens the global support panel in place — it is an overlay, not a
  // page, so there is nothing to route to. Inside the app this replaces the
  // floating launcher; on the login page the launcher stays, because there is
  // no sidebar there and someone who cannot sign in still has to reach support
  // (see the note beside <SupportWidget /> in App.js).
  {
    id:     'helper',
    label:  'Ndihmë',
    icon:   'HelpOutlineIcon',
    action: 'nexuma:open-support',
    // No permission gate: anyone signed in can ask for help.
  },
];

// ── Employee-only sidebar ─────────────────────────────────────────────────────
// Reorganized to mirror enterprise HR suites (BambooHR / HiBob / Workday): a flat
// "Ballina" entry followed by a small set of broad collapsible sections — Dosja
// Ime, Orari & Prezenca, Pushimet, Qendra e Aprovimeve, Performanca. This only
// regroups the SAME items (same routes, permissions and behaviour); no page,
// route or permission was changed. The SidebarRenderer renders flat items
// directly and `children` arrays as single-open collapsible sections; a section
// auto-hides when no child is permitted.
export const SIDEBAR_EMPLOYEE_CONFIG = [
  {
    id:    'home',
    label: 'Ballina',
    icon:  'HomeIcon',
    to:    '/',
    exact: true,
    requiresAny: [P.DASHBOARD_VIEW_OWN],
  },
  {
    // Dosja Ime — everything personal in one place: profile report, payslips,
    // advances and the handbook/documents the worker is allowed to read.
    id:          'emp-myfile',
    label:       'Dosja Ime',
    icon:        'FolderSpecialIcon',
    section:     true,
    requiresAny: [
      P.EMPLOYEES_VIEW_OWN, P.PAYROLL_VIEW_OWN, P.ATTENDANCE_VIEW_OWN, P.LEAVE_VIEW_OWN,
      P.DOCUMENTS_VIEW_OWN, P.DOCUMENTS_VIEW_DEPARTMENT, P.DOCUMENTS_VIEW_COMPANY,
    ],
    children: [
      /* ── Ana e punonjësit: JASHTË menyje për tani ──────────────────────
       * Bashkë me zërat te «Stafi» më lart. Faqet punojnë; vetëm hyrja te
       * menyja pret.
       *
       * {
       *   id: 'my-onboarding', label: 'Onboarding im',
       *   icon: 'HowToRegIcon', to: '/my-onboarding',
       * },
       * {
       *   id: 'my-tasks-emp', label: 'Detyrat e mia',
       *   icon: 'AssignmentTurnedInIcon', to: '/my-tasks',
       * },
       */
      {
        // Full personal report (Employee Detail), auto-scoped to the logged-in worker.
        id:          'my-profile',
        label:       'Profili im',
        icon:        'BadgeIcon',
        to:          '/home/reports/employee-detail',
        // No gate — own profile is always available to the logged-in user.
      },
      {
        id:          'my-payslips',
        label:       'Pagat e mia',
        icon:        'ReceiptLongIcon',
        to:          '/my-payslips',
        requiresAny: [P.PAYROLL_VIEW_OWN],
      },
      {
        // Fletëpagesa tregon shumat, jo arsyet. Këtu punonjësi sheh se ç'është
        // shtesë, ç'është avanc dhe ç'është ndalesë — dhe pse.
        id:          'my-adjustments',
        label:       'Rregullimet e mia',
        icon:        'TuneIcon',
        to:          '/my-adjustments',
        requiresAny: [P.PAYROLL_VIEW_OWN],
      },
      {
        // Çka mban punonjësi nga kompania. Lexim, pa asnjë veprim: dorëzimi dhe
        // kthimi janë vendim i HR-së, dhe një buton "kthej" këtu do të premtonte
        // diçka që sistemi nuk e kryen dot pa dikë që e pranon pajisjen.
        id:          'my-assets',
        label:       'Asetet e mia',
        icon:        'Inventory2Icon',
        to:          '/my-assets',
        requiresAny: [P.ASSETS_VIEW_OWN, P.ASSETS_VIEW_COMPANY],
      },
    ],
  },
  {
    // Orari & Prezenca — own attendance + monthly individual schedule.
    // Skano QR (/scan) and Check-in/Check-out live monitoring (/presence-live)
    // stay intentionally hidden from employees; the routes remain available.
    id:          'emp-time',
    label:       'Orari & Prezenca',
    icon:        'ScheduleIcon',
    section:     true,
    requiresAny: [P.ATTENDANCE_VIEW_OWN, P.REPORTS_VIEW_OWN, P.PROJECTS_VIEW_OWN],
    children: [
      {
        id:          'my-attendance',
        label:       'Prezenca ime',
        icon:        'FactCheckIcon',
        to:          '/my-attendance',
        requiresAny: [P.ATTENDANCE_VIEW_OWN],
      },
      {
        id:          'my-schedule-matrix',
        label:       'Orari Mujor Individual',
        icon:        'DateRangeIcon',
        to:          '/home/reports/work-schedule-matrix',
        requiresAny: [P.ATTENDANCE_VIEW_OWN, P.REPORTS_VIEW_OWN],
      },
    ],
  },
  {
    id:          'emp-leave',
    label:       'Pushimet',
    icon:        'EventAvailableIcon',
    section:     true,
    requiresAny: [P.LEAVE_VIEW_OWN, P.LEAVE_CREATE_OWN],
    children: [
      {
        id:          'my-leaves',
        label:       'Pushimet e mia',
        icon:        'EventAvailableIcon',
        to:          '/my-leaves',
        requiresAny: [P.LEAVE_VIEW_OWN],
      },
      {
        id:          'request-leave',
        label:       'Kërko Leje',
        icon:        'CalendarMonthIcon',
        to:          '/holidays/addholiday',
        requiresAny: [P.LEAVE_CREATE_OWN, P.LEAVE_VIEW_OWN],
      },
      {
        id:          'my-leave-report',
        label:       'Raporti i pushimeve',
        icon:        'AssessmentIcon',
        to:          '/my-leave-report',
        requiresAny: [P.LEAVE_VIEW_OWN],
      },
    ],
  },
  {
    // Qendra e Aprovimeve — promoted to its own top-level section. Same /inbox
    // route (legacy /attendance-corrections still resolves) as before.
    id:          'emp-approvals',
    label:       'Kërkesat dhe Aprovimet',
    icon:        'InboxIcon',
    section:     true,
    requiresAny: [P.ATTENDANCE_VIEW_OWN],
    children: [
      {
        id:          'emp-my-approvals',
        label:       'Aprovimet e Mia',
        icon:        'InboxIcon',
        to:          '/inbox',
        extraPaths:  ['/attendance-corrections'],
        requiresAny: [P.ATTENDANCE_VIEW_OWN],
      },
    ],
  },
  {
    id:          'emp-performance',
    label:       'Performanca',
    icon:        'EmojiEventsIcon',
    section:     true,
    requiresAny: [P.PERFORMANCE_VIEW_OWN],
    children: [
      {
        id:          'my-evaluation',
        label:       'Vlerësimi im',
        icon:        'EmojiEventsIcon',
        to:          '/my-evaluation',
        requiresAny: [P.PERFORMANCE_VIEW_OWN],
      },
    ],
  },


  // Same Helper entry for the employee sidebar — see the note in SIDEBAR_CONFIG.
  {
    id:     'helper',
    label:  'Ndihmë',
    icon:   'HelpOutlineIcon',
    action: 'nexuma:open-support',
  },
];

export default SIDEBAR_CONFIG;
